import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import './BlogPreview.css';

import studioImage from '../../media/studio.png';
import civicsImage from '../../media/blog_civics.png';
import tokenImage from '../../media/blog_token.png';
import managerImage from '../../media/blog_manager.png';
import hitlImage from '../../media/blog_hitl.png';
import infoImage from '../../media/blog_information.png';
import partyImage from '../../media/blog_party.png';

type Post = {
  tag: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  slug: string;
  file: string;
  live: boolean;
};

const POST_IMAGES: Record<string, string> = {
  'why-we-built-codetunez': studioImage,
  'building-civics-tutor': civicsImage,
  'token-kit': tokenImage,
  'engineering-manager-agentic-era': managerImage,
  'hitl-hindrance': hitlImage,
  'hitl-bottleneck': infoImage,
  'where-code-meets-art': partyImage,
};

const BlogPreview: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [centerIndex, setCenterIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadPosts = async () => {
      try {
        const response = await fetch('/blog/index.json');
        const data = (await response.json()) as Post[];
        if (isMounted) {
          setPosts(data.filter((p) => p.live));
          setCenterIndex(0);
        }
      } catch {
        if (isMounted) {
          setPosts([]);
        }
      }
    };

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const visiblePosts = useMemo(() => {
    if (!posts.length) {
      return [] as Array<{ post: Post; offset: number }>;
    }

    const visibleCount = Math.min(3, posts.length);
    const startOffset = -Math.floor(visibleCount / 2);

    return Array.from({ length: visibleCount }, (_, i) => {
      const offset = startOffset + i;
      const wrappedIndex = (centerIndex + offset + posts.length) % posts.length;
      return { post: posts[wrappedIndex], offset };
    });
  }, [posts, centerIndex]);

  const handlePrev = () => {
    if (!posts.length) {
      return;
    }
    setCenterIndex((prev) => (prev - 1 + posts.length) % posts.length);
  };

  const handleNext = () => {
    if (!posts.length) {
      return;
    }
    setCenterIndex((prev) => (prev + 1) % posts.length);
  };

  return (
    <section id="blog" className="blog">
      <div className="container">
        <div className="blog__header">
          <div>
            <p className="section-label">Blog</p>
            <h2 className="section-title">
              Latest from the <span className="gradient-text">Studio</span>
            </h2>
          </div>
          <Link to="/blog" className="blog__view-all">
            View All Posts <FiArrowRight />
          </Link>
        </div>

        <div className="blog__carousel">
          <button className="blog__carousel-nav" onClick={handlePrev} aria-label="Previous post">
            <FiArrowLeft />
          </button>

          <div className="blog__track">
            {visiblePosts.map(({ post, offset }, i) => {
              const tone = Math.abs(offset);
              const toneClass = tone === 0 ? 'is-center' : tone === 1 ? 'is-near' : 'is-far';

              return (
                <Link to={`/blog/${post.slug}`} className="blog__card-link-wrap" key={`${post.slug}-${offset}-${i}`}>
                  <article className={`blog__card ${toneClass}`}>
                    <div 
                      className="blog__card-img-placeholder"
                      style={{ backgroundImage: `url(${POST_IMAGES[post.slug] || studioImage})` }}
                    />
                    <div className="blog__card-body">
                      <span className="blog__card-tag">{post.tag}</span>
                      <h3 className="blog__card-title">{post.title}</h3>
                      <p className="blog__card-excerpt">{post.excerpt}</p>
                      <span className="blog__card-date">{post.date}</span>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>

          <button className="blog__carousel-nav" onClick={handleNext} aria-label="Next post">
            <FiArrowRight />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
